import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownButton, DropdownMenu, DropdownMenuItem } from '@unified-checkout/design-system';
import { useLangStore } from '@app/dashboard/store/lang';
import { SecondaryFiltersProps, TSecondaryFilters } from '@app/dashboard/types/innerLayout/reports';
import { getMultiSelectorBtnLabel } from '../utils';
import styles from '../generateReport.module.scss';

const SecondaryFilters = ({ secondaryFilters, updateFilters, type, disabled }: SecondaryFiltersProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
    
  const [localSelectedOptions, setLocalSelectedOptions] = useState<TSecondaryFilters>(secondaryFilters);

  const handleApply = () => {
    updateFilters(localSelectedOptions);
  };

  const handleToggle = (isOpen: boolean) => {
    if (!isOpen) setLocalSelectedOptions(secondaryFilters);
  };

  return (
    <div
        className={`${styles['filters-section__secondary-row']} ${
          type?.hasFilters ? styles['filters-section__secondary-row__visible'] : styles['filters-section__secondary-row__hidden']
        }`}
      >
        {Object.values(secondaryFilters).map((filterConfig, index) => (
          <div
            key={index}
            className={
              styles['filters-section__secondary-row__filter-dropdown']
            }
          >
            <Dropdown
              label={t(`innerLayout.reportsPage.${filterConfig.placeholder}`)}
            >
              <DropdownButton
                type="input"
                text={getMultiSelectorBtnLabel(
                  secondaryFilters[filterConfig.id]?.selectedOptions || [],
                  filterConfig.options,
                  t,
                  t(
                    `innerLayout.reportsPage.${secondaryFilters[filterConfig.id]?.allLabel}`
                  ),
                )}
                placeholder={t(
                  'innerLayout.reportsPage.reportTypesFilter.title'
                )}
              />
              <DropdownMenu 
                type="checkboxes"
                lang={lang}
                onApplyHandler={handleApply}
                onToggleHandler={handleToggle}>
                <DropdownMenuItem
                  key={`${filterConfig.id}-secondary-filter-select-all`}
                  type='checkbox'
                  checkboxIcon="dash"
                  id={`${filterConfig.id}-select-all-${index}`}
                  text={`${localSelectedOptions[filterConfig.id]?.selectedOptions.length || 0} ${t('innerLayout.selected')}`}
                  checked={(localSelectedOptions[filterConfig.id]?.selectedOptions.length || 0) === filterConfig?.options?.length}
                  onCheckboxItemClickHandler={(checked) => {
                    setLocalSelectedOptions((prevFilters) => ({
                      ...prevFilters,
                      [filterConfig.id]: {
                        ...prevFilters[filterConfig.id],
                        selectedOptions: checked ? filterConfig.options : [],
                      },
                    }));
                  }}
                  closeOnItemClick={false}
                />
                {filterConfig.options.map((option, optionIndex) => {
                  const optionLabel = option.label;
                  const optionValue = option.value;
                  const filterId = filterConfig.id;
                  return (
                    <DropdownMenuItem
                      text={optionLabel}
                      key={`${filterId}-secondary-filter-${optionValue}`}
                      type="checkbox"
                      id={`${optionValue}-option-${optionIndex}`}
                      checked={
                        !!(localSelectedOptions[filterId]?.selectedOptions).some(
                          (opt) => opt.value === optionValue
                        )
                      }
                      onCheckboxItemClickHandler={(checked) =>
                        setLocalSelectedOptions((prevFilters) => ({
                          ...prevFilters,
                          [filterId]: {
                            ...prevFilters[filterId],
                            selectedOptions: checked
                              ? [
                                  ...prevFilters[filterId].selectedOptions,
                                  { label: optionLabel, value: optionValue },
                                ]
                              : prevFilters[filterId]?.selectedOptions?.filter(
                                  (option) =>
                                    (option.value || option) !== optionValue
                                ),
                          },
                        }))
                      }
                      closeOnItemClick={false}
                      disabled={disabled}
                    />
                  );
                })}
              </DropdownMenu>
            </Dropdown>
          </div>
        ))}
      </div>
  );
};

export default SecondaryFilters;
